import { CloseOutlined } from '@ant-design/icons';
import { App, Button, ConfigProvider, Modal, Popconfirm, Space, Spin, Table } from 'antd';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { Eye, Copy } from 'lucide-react';
import { Activity, useEffect, useState } from 'react';
import { addOrder, addOrderItem, getInvoices, getOrderLink, getOrdersById } from '../../redux/actions/order';
import { formatCurrency, getApplicableTierPrice } from '../../utils';
import { useLocation, useNavigate } from 'react-router-dom';
import { getRoutes } from '../../routes/Routes';
import { encodeId } from '../../utils/idEncoder';
import { getProductDetails } from '../../redux/actions/products';

const KioskOrderHistory = ({ customerId, onCloneComplete }) => {
    const { modal: antdModal, message } = App.useApp();
    const location = useLocation();
    const navigate = useNavigate();
    const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);
    const [pdfLink, setPdfLink] = useState('');
    const [isIframeLoading, setIsIframeLoading] = useState(true);
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [isCloning, setIsCloning] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 50,
        totalRecords: 0,
        totalPages: 0,
    });

    const handleCloneOrder = async (recordToClone = null) => {
        const record = recordToClone?.id ? recordToClone : selectedRecord;
        if (!record) return;
        setIsCloning(true);
        try {
            const detailsRes = await getOrdersById(record?.id || record?.internal_id, { toast: false });
            if (!detailsRes?.ack || !detailsRes?.order_details?.length) {
                return;
            }

            const newOrderRes = await addOrder({
                customer_netsuite_id: customerId,
                type: detailsRes.order?.type || 1,
                order_date: dayjs().format('YYYY-MM-DD'),
                is_frontdesk: 0,
                is_kiosk: 1,
            }, { toast: false });

            const newOrderId = newOrderRes?.record_id || newOrderRes?.order_id;
            if (!newOrderId) return;

            await Promise.all(detailsRes.order_details.map(async (item) => {
                const details = await getProductDetails({ id: item.product_netsuite_id || item.netsuite_id, details: 1, loading: false });
                const tiers = details?.product || [];

                const dynamicPrice = getApplicableTierPrice({
                    ...item,
                    tiers,
                    uom: item.uom || 'Case',
                    product_case_quantity: item.case_quantity || item.product_case_quantity || 1,
                    quantity: item.quantity,
                    price_level: item.price_level,
                });

                const newUnitPrice = dynamicPrice?.tier_price || dynamicPrice?.unit_price || item.unit_price;

                const body = {
                    order_id: newOrderId,
                    product_netsuite_id: item.product_netsuite_id || item.netsuite_id,
                    quantity: item.quantity,
                    unit_price: newUnitPrice,
                    price_level: item.price_level,
                    uom: item.uom || 'Case',
                };
                return addOrderItem(body, item, false, { toast: false });
            }));

            setIsPdfModalOpen(false);
            if (onCloneComplete) {
                onCloneComplete();
            }
            navigate(
                {
                    pathname: getRoutes().kiosk.route(),
                    search: `?${new URLSearchParams({
                        orderId: encodeId(String(newOrderId)),
                    })}`,
                },
                {
                    state: { ...location.state, openCart: true },
                    replace: true,
                }
            );
        } finally {
            setIsCloning(false);
        }
    };

    const fetchInvoices = async ({ page = 1, start_date, end_date } = {}) => {
        if (!customerId) return;
        setLoading(true);
        try {
            const res = await getInvoices({
                customer_netsuite_id: customerId,
                page,
                start_date,
                end_date,
            }, {
                toast: false,
                loading: false,
            });

            setInvoices(res?.invoices || []);
            if (res?.ack) {
                setPagination((prev) => ({
                    ...prev,
                    current: page,
                    totalPages: Math.ceil(res.total_count / prev.pageSize),
                    totalRecords: res.total_count,
                }));
            }
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: 'Date',
            dataIndex: 'order_date',
            key: 'order_date',
            render: (date) => date && dayjs(date).format('DD-MM-YYYY'),
        },
        {
            title: 'Internal Code',
            render: (record) => {
                return (
                    <div className="flex align-items-center gap-1">
                        {record.code}
                    </div>
                );
            },
        },
        {
            title: 'Invoice Code',
            key: 'invoice_code',
            render: (_, record) => {
                const hasOrder = Boolean(record?.order_code);
                const hasInvoice = Boolean(record?.invoice_code);

                return (
                    (hasOrder || hasInvoice) && (
                        <div>
                            {hasOrder && (
                                <div className="flex align-items-center gap-1">
                                    {record.order_code}
                                </div>
                            )}

                            {hasInvoice && (
                                <div className="flex align-items-center gap-1">
                                    {record.invoice_code}
                                </div>
                            )}
                        </div>
                    )
                );
            },
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            render: (value) => {
                if (value === 1) return 'Collection';
                if (value === 2) return 'Delivery';
                return '-';
            },
        },
        {
            title: 'Order Value',
            dataIndex: 'order_value',
            key: 'order_value',
            render: (text) => formatCurrency(text || 0),
        },
        {
            key: 'actions',
            align: 'end',
            fixed: 'right',
            render: (text, record) => (
                <Space size="small" className="kiosk-order-table">
                    <Activity mode="visible">
                        <ConfigProvider theme={{ token: { colorPrimary: '#f58220' } }}>
                            <Space size="small">
                                <Button
                                    size="medium"
                                    color="primary"
                                    variant="solid"
                                    icon={<Copy />}
                                    onClick={() => {
                                        antdModal.confirm({
                                            centered: true,
                                            zIndex: 2200,
                                            title: 'Clone Order',
                                            content: 'Are you sure you want to clone this order? A new order will be created with the latest applicable prices.',
                                            okText: 'Yes, clone it',
                                            cancelText: 'No, cancel',
                                            okButtonProps: {
                                                style: { backgroundColor: '#f58220', borderColor: '#f58220', color: '#fff' }
                                            },
                                            onOk: () => handleCloneOrder(record),
                                        });
                                    }}
                                />
                                <Button
                                    size="medium"
                                    color="primary"
                                    variant="solid"
                                    icon={<Eye />}
                                    onClick={async () => {
                                        setSelectedRecord(record);
                                        setPdfLink('');
                                        setIsIframeLoading(true);
                                        setIsPdfModalOpen(true);
                                        const res = await getOrderLink(record?.internal_id || record?.id);
                                        if (res?.ack && res.link) {
                                            setPdfLink(res.link);
                                        } else {
                                            setIsIframeLoading(false);
                                            setIsPdfModalOpen(false);
                                            message.error('Failed to load invoice details.');
                                        }
                                    }}
                                />
                            </Space>
                        </ConfigProvider>
                    </Activity>
                </Space>
            ),
        },
    ];

    useEffect(() => {
        fetchInvoices({
            page: pagination.current,
            start_date: null,
            end_date: null,
        });
    }, [customerId]);

    return (
        <div>
            <Table
                size="small" className="kiosk-order-table"
                columns={columns}
                rowKey={(record) => record?.id}
                dataSource={invoices}
                loading={loading}
                pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.totalRecords,
                    showSizeChanger: false,
                    responsive: true,
                    size: 'small',
                    showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} Records`,
                    onChange: (page) => {
                        fetchInvoices({
                            page,
                            start_date: null,
                            end_date: null,
                        });
                    },
                }}
                scroll={{ y: 'calc(100vh - 500px)' }}
                components={{
                    body: {
                        cell: ({ className, ...rest }) => (
                            <td {...rest} className={invoices?.length && clsx(className, 'py-1')} />
                        ),
                    },
                }}
            />

            {/* PDF Modal */}
            <ConfigProvider theme={{ token: { colorPrimary: '#f58220' } }}>
                <Modal
                    open={isPdfModalOpen}
                    onCancel={() => setIsPdfModalOpen(false)}
                    footer={null}
                    closable={false}
                    width={800}
                    centered
                    destroyOnHidden
                    zIndex={2100}
                    styles={{
                        body: { height: '85vh', padding: 0, display: 'flex', flexDirection: 'column' },
                        content: { padding: 0, overflow: 'hidden', borderRadius: '16px', background: '#ffffff' }
                    }}
                >
                    {/* Clean Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', background: '#ffffff', borderBottom: '1px solid #e5e7eb' }}>
                        <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: '#111827' }}>
                            Invoice Details
                        </h3>
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                            <Button
                                type="primary"
                                loading={isCloning}
                                onClick={() => {
                                    antdModal.confirm({
                                        centered: true,
                                        zIndex: 2200,
                                        title: 'Clone Order',
                                        content: 'Are you sure you want to clone this order? A new order will be created with the latest applicable prices.',
                                        okText: 'Yes, clone it',
                                        cancelText: 'No, cancel',
                                        okButtonProps: {
                                            style: { backgroundColor: '#f58220', borderColor: '#f58220', color: '#fff' }
                                        },
                                        onOk: handleCloneOrder,
                                    });
                                }}
                                style={{ borderRadius: '8px' }}
                            >
                                Clone Order
                            </Button>
                            <Button
                                type="text"
                                icon={<CloseOutlined style={{ fontSize: '20px' }} />}
                                onClick={() => setIsPdfModalOpen(false)}
                                style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            />
                        </div>
                    </div>

                    {/* Iframe Container */}
                    <div style={{ flex: 1, position: 'relative', overflow: 'hidden', background: '#ffffff' }}>
                        {isIframeLoading && (
                            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}>
                                <Spin size="large" />
                            </div>
                        )}
                        {pdfLink && (
                            <iframe
                                src={`${pdfLink}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                                width="100%"
                                height="100%"
                                style={{ border: 'none', display: 'block', backgroundColor: '#ffffff' }}
                                title="Invoice Document"
                                onLoad={() => setIsIframeLoading(false)}
                            />
                        )}
                    </div>
                </Modal>
            </ConfigProvider>
        </div>
    );
};

export default KioskOrderHistory;


